import { useState } from "react";
import { Certification } from "../../../../types/certificate";
import { fmt, highlight, isExpiringSoon, StatusBadge } from "../../../../helpers/certificate";
import { IconBtn } from "../../../common/UI/Button/IconBtn";

export default function CertCard({
    cert, selected, onSelect, onView, onEdit, onDelete, searchQuery,
}: {
    cert: Certification; selected: boolean;
    onSelect: () => void; onView: () => void; onEdit: () => void; onDelete: () => void;
    searchQuery: string;
}) {
    const [hov, setHov] = useState(false);
    return (
        <div
            onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
            style={{
                background: selected ? "rgba(191,61,12,0.06)" : "var(--card)",
                border: `1px solid ${selected ? "rgba(191,61,12,0.25)" : hov ? "rgba(255,255,255,0.12)" : "var(--border)"}`,
                borderRadius: 14, overflow: "hidden", display: "flex", flexDirection: "column",
                transition: "all 0.15s",
                boxShadow: hov ? "0 4px 20px rgba(0,0,0,0.3)" : "none",
            }}
        >
            {/* Image area */}
            <div style={{
                height: 130, background: "rgba(255,255,255,0.02)", position: "relative",
                borderBottom: "1px solid var(--border)",
                display: "flex", alignItems: "center", justifyContent: "center",
            }}>
                {cert.imageUrl ? (
                    <img src={cert.imageUrl} alt={cert.title}
                        style={{ maxHeight: 110, maxWidth: "100%", objectFit: "contain", padding: 8 }} />
                ) : (
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, opacity: 0.4 }}>
                        <span style={{ fontSize: 36 }}>🏅</span>
                        <span style={{ fontSize: 11, color: "var(--text3)" }}>No image</span>
                    </div>
                )}

                {/* Checkbox overlay */}
                <div style={{ position: "absolute", top: 10, left: 10 }}>
                    <input type="checkbox" checked={selected} onChange={onSelect}
                        style={{ accentColor: "var(--or)", cursor: "pointer", width: 14, height: 14 }} />
                </div>

                {/* Expiring soon badge */}
                {isExpiringSoon(cert.expiryDate) && (
                    <div style={{
                        position: "absolute", top: 10, right: 10,
                        fontSize: 10, fontWeight: 700, letterSpacing: "0.5px",
                        background: "rgba(245,166,35,0.15)", border: "1px solid rgba(245,166,35,0.3)",
                        color: "#f5a623", borderRadius: 4, padding: "2px 6px",
                    }}>EXPIRING SOON</div>
                )}
            </div>

            {/* Content */}
            <div style={{ padding: "14px 16px", flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text)", lineHeight: 1.35, marginBottom: 4 }}>
                            {highlight(cert.title, searchQuery)}
                        </div>
                        <div style={{ fontSize: 12, color: "var(--text3)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {cert.issuedBy}
                        </div>
                    </div>
                    <StatusBadge status={cert.status} expiry={cert.expiryDate} />
                </div>

                <div style={{
                    fontSize: 12, color: "var(--text3)", lineHeight: 1.5,
                    display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
                }}>
                    {cert.description}
                </div>

                <div style={{ display: "flex", gap: 12, marginTop: "auto", paddingTop: 4 }}>
                    <div>
                        <div style={{ fontSize: 10, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 2 }}>Issued</div>
                        <div style={{ fontSize: 12, color: "var(--text2)" }}>{fmt(cert.issuedDate)}</div>
                    </div>
                    {cert.expiryDate && (
                        <div>
                            <div style={{ fontSize: 10, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 2 }}>Expires</div>
                            <div style={{ fontSize: 12, color: isExpiringSoon(cert.expiryDate) ? "#f5a623" : "var(--text2)" }}>
                                {fmt(cert.expiryDate)}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Actions */}
            <div style={{
                display: "flex", gap: 6, padding: "10px 14px",
                borderTop: "1px solid var(--border)", background: "rgba(255,255,255,0.01)",
            }}>
                <IconBtn title="View" onClick={onView}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
                    </svg>
                </IconBtn>
                <IconBtn title="Edit" onClick={onEdit}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                </IconBtn>
                <div style={{ flex: 1 }} />
                <IconBtn title="Delete" danger onClick={onDelete}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                        <path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4h6v2" />
                    </svg>
                </IconBtn>
            </div>
        </div>
    );
}
